/**
 * @page LeadFinder
 * @description Prospecting-Tool: Firmen über Apollo.io recherchieren, qualifizieren, als Leads importieren.
 * Tabs: Suchprofile (CRUD) | Ergebnisse (Tabelle mit Bulk-Import, Enrichment, AI-Research).
 */
import { useState, useEffect, useCallback } from 'react';
import useTabParam from '../../hooks/useTabParam';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { ToggleSwitch } from '../../components/ui';
import {
 Search, Plus, Play, Trash2, Download, ArrowRight, RefreshCw,
 Globe, Users, Building2, CheckCircle, XCircle, Loader, ExternalLink,
 Mail, Phone, Sparkles, ChevronRight, X, Eye, Target, Zap,
 Settings, AlertTriangle, Key, Check,
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function api(url, opts = {}) {
 return fetch(`${API}${url}`, { credentials: 'include', ...opts }).then(r => r.json());
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════

export default function LeadFinder() {
 const { t } = useLanguage();
 const { user } = useAuth();
 const isAdmin = user?.rolle === 'admin';
 const [activeTab, setActiveTab] = useTabParam('profiles');
 const [profiles, setProfiles] = useState([]);
 const [selectedProfileId, setSelectedProfileId] = useState(null);
 const [showEditor, setShowEditor] = useState(false);
 const [editingProfile, setEditingProfile] = useState(null);
 const [loading, setLoading] = useState(true);
 const [hasApolloKey, setHasApolloKey] = useState(true); // Optimistisch starten

 const loadProfiles = useCallback(async () => {
 setLoading(true);
 try { setProfiles(await api('/api/lead-finder/profiles')); }
 catch { setProfiles([]); }
 setLoading(false);
 }, []);

 // Apollo-Key-Status prüfen (nur Admin)
 const checkApolloKey = useCallback(async () => {
 if (!isAdmin) return;
 try {
 const s = await api('/api/lead-finder/settings');
 setHasApolloKey(s.has_api_key);
 } catch { /* ignore */ }
 }, [isAdmin]);

 useEffect(() => { loadProfiles(); checkApolloKey(); }, [loadProfiles, checkApolloKey]);

 function openResults(profileId) {
 setSelectedProfileId(profileId);
 setActiveTab('results');
 }

 const tabs = [
 { id: 'profiles', label: t('leadFinder.profiles'), count: profiles.length },
 { id: 'results', label: t('leadFinder.results'), count: null },
 ...(isAdmin ? [{ id: 'settings', label: t('leadFinder.settings'), count: null }] : []),
 ];

 return (
 <div className="p-6">
 {/* Apollo Key Banner */}
 {isAdmin && !hasApolloKey && activeTab !== 'settings' && (
 <ApolloKeyBanner onGoToSettings={() => setActiveTab('settings')} />
 )}

 {/* Header */}
 <div className="flex items-center justify-between mb-6">
 <div>
 <h1 className="text-[22px] font-semibold text-[var(--text-primary)]">
 {t('leadFinder.title')}
 </h1>
 <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">
 {t('leadFinder.subtitle')}
 </p>
 </div>
 <button
 onClick={() => { setEditingProfile(null); setShowEditor(true); }}
 className="flex items-center gap-2 px-4 py-2 rounded-2xl text-white text-[13px] font-medium"
 style={{ background: 'var(--primary)' }}
 >
 <Plus className="w-4 h-4" />
 {t('leadFinder.newProfile')}
 </button>
 </div>

 {/* Tabs */}
 <div className="flex gap-1 border-b border-[var(--border-table,#F3F4F6)] mb-6">
 {tabs.map(tab => (
 <button
 key={tab.id}
 onClick={() => setActiveTab(tab.id)}
 className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
 activeTab === tab.id
 ? 'border-[#338edc] text-[#338edc]'
 : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
 }`}
 >
 {tab.label}
 {tab.count !== null && (
 <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
 activeTab === tab.id
 ? 'bg-[#EBF4FD] text-[#338edc]'
 : 'bg-[var(--bg-inset)] text-[var(--text-tertiary)]'
 }`}>{tab.count}</span>
 )}
 </button>
 ))}
 </div>

 {/* Profiles Tab */}
 {activeTab === 'profiles' && (
 <ProfilesList
 profiles={profiles}
 loading={loading}
 onSelect={(p) => openResults(p.id)}
 onEdit={(p) => { setEditingProfile(p); setShowEditor(true); }}
 onDelete={async (id) => {
 if (!confirm(t('leadFinder.deleteConfirm'))) return;
 await api(`/api/lead-finder/profiles/${id}`, { method: 'DELETE' });
 loadProfiles();
 }}
 onSearch={async (id) => {
 await fetch(`${API}/api/lead-finder/search/${id}`, {
 method: 'POST', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ clearPrevious: true }),
 });
 openResults(id);
 }}
 />
 )}

 {/* Results Tab */}
 {activeTab === 'results' && selectedProfileId && (
 <ResultsTable profileId={selectedProfileId} />
 )}

 {activeTab === 'results' && !selectedProfileId && (
 <div className="text-center py-16 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-[12px]">
 <Search className="w-10 h-10 mx-auto mb-3 text-[var(--text-tertiary)]" />
 <div className="text-[13px] text-[var(--text-secondary)]">{t('leadFinder.selectProfile')}</div>
 </div>
 )}

 {/* Settings Tab (Admin only) */}
 {activeTab === 'settings' && isAdmin && (
 <LeadFinderSettings onKeyChanged={checkApolloKey} />
 )}

 {/* Profile Editor Modal */}
 {showEditor && (
 <ProfileEditor
 profile={editingProfile}
 onClose={() => setShowEditor(false)}
 onSave={() => { setShowEditor(false); loadProfiles(); }}
 />
 )}
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════════════════
// PROFILES LIST
// ═══════════════════════════════════════════════════════════════════════════

function ProfilesList({ profiles, loading, onSelect, onEdit, onDelete, onSearch }) {
 const { t } = useLanguage();

 if (loading) {
 return <div className="text-center py-12 text-[var(--text-tertiary)] text-[13px]">Loading...</div>;
 }

 if (profiles.length === 0) {
 return (
 <div className="text-center py-16 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-[12px]">
 <Target className="w-10 h-10 mx-auto mb-3 text-[var(--text-tertiary)]" />
 <div className="text-[13px] font-medium text-[var(--text-secondary)]">{t('leadFinder.noProfiles')}</div>
 <div className="text-[11px] text-[var(--text-tertiary)] mt-1">{t('leadFinder.noProfilesDesc')}</div>
 </div>
 );
 }

 return (
 <div className="space-y-3">
 {profiles.map(p => {
 const filters = p.filters || {};
 return (
 <div key={p.id} className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-[12px] p-5 group hover:border-[var(--border-default,#E5E7EB)] transition-colors">
 <div className="flex items-center justify-between">
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2 mb-1.5">
 <span className="text-[14px] font-semibold text-[var(--text-primary)]">{p.name}</span>
 {p.last_run_status === 'running' && (
 <span className="flex items-center gap-1 text-[10px] font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
 <Loader className="w-2.5 h-2.5 animate-spin" /> {t('leadFinder.searching')}
 </span>
 )}
 {p.last_run_status === 'completed' && p.result_count > 0 && (
 <span className="text-[10px] font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
 {p.result_count} {t('leadFinder.resultsFound')}
 </span>
 )}
 </div>

 {/* Filter Badges */}
 <div className="flex flex-wrap gap-1.5">
 {(filters.countries || []).map(c => (
 <span key={c} className="text-[10px] px-2 py-0.5 rounded-lg bg-blue-50 text-blue-700 font-medium">{c}</span>
 ))}
 {(filters.industries || []).map(ind => (
 <span key={ind} className="text-[10px] px-2 py-0.5 rounded-lg bg-[#EBF4FD] text-[#338edc] font-medium">{ind}</span>
 ))}
 {(filters.technologies || []).map(tech => (
 <span key={tech} className="text-[10px] px-2 py-0.5 rounded-lg bg-cyan-50 text-cyan-700 font-medium">{tech}</span>
 ))}
 {filters.employee_min && (
 <span className="text-[10px] px-2 py-0.5 rounded-lg bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)]">
 {filters.employee_min}–{filters.employee_max || '∞'} MA
 </span>
 )}
 {(filters.keywords || []).length > 0 && (
 <span className="text-[10px] px-2 py-0.5 rounded-lg bg-amber-50 text-amber-700 font-medium">
 {filters.keywords.join(', ')}
 </span>
 )}
 </div>

 {p.last_run_at && (
 <div className="text-[10px] text-[var(--text-tertiary)] mt-1.5">
 {t('leadFinder.lastRun')}: {new Date(p.last_run_at).toLocaleString('de-AT')}
 </div>
 )}
 </div>

 <div className="flex items-center gap-1.5">
 <button onClick={() => onSearch(p.id)} title={t('leadFinder.search')}
 className="p-2 rounded-lg text-[#338edc] hover:bg-[#EBF4FD] transition-colors">
 <Play className="w-4 h-4" />
 </button>
 <button onClick={() => onSelect(p)} title={t('leadFinder.results')}
 className="p-2 rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] transition-colors">
 <Eye className="w-4 h-4" />
 </button>
 <button onClick={() => onEdit(p)} title={t('common.edit')}
 className="p-2 rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] transition-colors opacity-0 group-hover:opacity-100">
 <RefreshCw className="w-3.5 h-3.5" />
 </button>
 <button onClick={() => onDelete(p.id)} title={t('common.delete')}
 className="p-2 rounded-lg text-[var(--text-tertiary)] hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100">
 <Trash2 className="w-3.5 h-3.5" />
 </button>
 </div>
 </div>
 </div>
 );
 })}
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════════════════
// PROFILE EDITOR MODAL
// ═══════════════════════════════════════════════════════════════════════════

const COUNTRIES = [
 { code: 'AT', name: 'Austria' },
 { code: 'DE', name: 'Germany' },
 { code: 'CH', name: 'Switzerland' },
 { code: 'NL', name: 'Netherlands' },
 { code: 'FR', name: 'France' },
 { code: 'IT', name: 'Italy' },
 { code: 'ES', name: 'Spain' },
 { code: 'UK', name: 'UK' },
 { code: 'PL', name: 'Poland' },
 { code: 'SE', name: 'Sweden' },
];

function ProfileEditor({ profile, onClose, onSave }) {
 const { t } = useLanguage();
 const [name, setName] = useState(profile?.name || '');
 const [filters, setFilters] = useState(profile?.filters || {
 countries: [], industries: [], keywords: [],
 employee_min: null, employee_max: null,
 technologies: [], volume_min: null, volume_max: null,
 });
 const [industries, setIndustries] = useState([]);
 const [technologies, setTechnologies] = useState([]);
 const [saving, setSaving] = useState(false);

 useEffect(() => {
 api('/api/lead-finder/industries').then(d => setIndustries(d.industries || []));
 api('/api/lead-finder/technologies').then(d => setTechnologies(d.technologies || []));
 }, []);

 function updateFilter(key, value) {
 setFilters(prev => ({ ...prev, [key]: value }));
 }

 function toggleArrayItem(key, item) {
 setFilters(prev => {
 const arr = prev[key] || [];
 return { ...prev, [key]: arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item] };
 });
 }

 async function handleSave() {
 if (!name.trim()) return;
 setSaving(true);
 const method = profile?.id ? 'PUT' : 'POST';
 const url = profile?.id ? `/api/lead-finder/profiles/${profile.id}` : '/api/lead-finder/profiles';
 await fetch(`${API}${url}`, {
 method, credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ name: name.trim(), filters, sources: ['apollo'] }),
 });
 setSaving(false);
 onSave();
 }

 return (
 <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
 <div className="bg-[var(--bg-card)] rounded-[12px] border border-[var(--border-card)] w-full max-w-xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
 {/* Header */}
 <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card)]">
 <div className="text-[15px] font-semibold text-[var(--text-primary)]">
 {profile?.id ? t('leadFinder.editProfile') : t('leadFinder.newProfile')}
 </div>
 <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
 <X className="w-4 h-4" />
 </button>
 </div>

 <div className="p-6 overflow-y-auto max-h-[calc(85vh-130px)] space-y-5">
 {/* Name */}
 <div>
 <label className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] block mb-1.5">
 {t('leadFinder.profileName')}
 </label>
 <input
 value={name}
 onChange={e => setName(e.target.value)}
 placeholder="z.B. Mid-Scale Beauty DACH"
 className="w-full px-3 py-2.5 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] focus:outline-none focus:border-[#338edc]"
 />
 </div>

 {/* Countries */}
 <div>
 <label className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] block mb-1.5">
 {t('leadFinder.countries')}
 </label>
 <div className="flex flex-wrap gap-2">
 {COUNTRIES.map(c => (
 <button
 key={c.code}
 onClick={() => toggleArrayItem('countries', c.code)}
 className={`px-3 py-1.5 rounded-lg text-[12px] font-medium transition-all ${
 (filters.countries || []).includes(c.code)
 ? 'bg-[#338edc] text-white'
 : 'bg-[var(--bg-inset)] text-[var(--text-secondary)] border border-[var(--border-card)] hover:border-[#338edc]'
 }`}
 >
 {c.code}
 </button>
 ))}
 </div>
 </div>

 {/* Industries */}
 <div>
 <label className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] block mb-1.5">
 {t('leadFinder.industries')}
 </label>
 <div className="flex flex-wrap gap-1.5">
 {industries.map(ind => (
 <button
 key={ind}
 onClick={() => toggleArrayItem('industries', ind)}
 className={`px-2.5 py-1 rounded-lg text-[11px] transition-all ${
 (filters.industries || []).includes(ind)
 ? 'bg-[#EBF4FD] text-[#338edc] font-semibold border border-[#B8D8F5]'
 : 'bg-[var(--bg-inset)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)]'
 }`}
 >
 {ind}
 </button>
 ))}
 </div>
 </div>

 {/* Keywords */}
 <div>
 <label className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] block mb-1.5">
 {t('leadFinder.keywords')}
 </label>
 <input
 value={(filters.keywords || []).join(', ')}
 onChange={e => updateFilter('keywords', e.target.value.split(',').map(k => k.trim()).filter(Boolean))}
 placeholder="z.B. Bio, Naturkosmetik, DTC, Vegan"
 className="w-full px-3 py-2.5 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] focus:outline-none focus:border-[#338edc]"
 />
 </div>

 {/* Employee Range */}
 <div>
 <label className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] block mb-1.5">
 {t('leadFinder.companySize')}
 </label>
 <div className="flex items-center gap-2">
 <input
 type="number" min="0"
 value={filters.employee_min || ''}
 onChange={e => updateFilter('employee_min', parseInt(e.target.value) || null)}
 placeholder="Min"
 className="w-24 px-3 py-2.5 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] focus:outline-none focus:border-[#338edc]"
 />
 <span className="text-[var(--text-tertiary)] text-[13px]">—</span>
 <input
 type="number" min="0"
 value={filters.employee_max || ''}
 onChange={e => updateFilter('employee_max', parseInt(e.target.value) || null)}
 placeholder="Max"
 className="w-24 px-3 py-2.5 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] focus:outline-none focus:border-[#338edc]"
 />
 <span className="text-[11px] text-[var(--text-tertiary)]">{t('leadFinder.employees')}</span>
 </div>
 </div>

 {/* Technologies */}
 <div>
 <label className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] block mb-1.5">
 {t('leadFinder.technologies')}
 </label>
 <div className="flex flex-wrap gap-1.5">
 {technologies.map(tech => (
 <button
 key={tech}
 onClick={() => toggleArrayItem('technologies', tech)}
 className={`px-2.5 py-1 rounded-lg text-[11px] transition-all ${
 (filters.technologies || []).includes(tech)
 ? 'bg-cyan-50 text-cyan-700 font-semibold border border-cyan-200'
 : 'bg-[var(--bg-inset)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)]'
 }`}
 >
 {tech}
 </button>
 ))}
 </div>
 </div>

 {/* Volume */}
 <div>
 <label className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] block mb-1.5">
 {t('leadFinder.volume')}
 </label>
 <div className="flex items-center gap-2">
 <input
 type="number" min="0"
 value={filters.volume_min || ''}
 onChange={e => updateFilter('volume_min', parseInt(e.target.value) || null)}
 placeholder="Min"
 className="w-24 px-3 py-2.5 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] focus:outline-none focus:border-[#338edc]"
 />
 <span className="text-[var(--text-tertiary)] text-[13px]">—</span>
 <input
 type="number" min="0"
 value={filters.volume_max || ''}
 onChange={e => updateFilter('volume_max', parseInt(e.target.value) || null)}
 placeholder="Max"
 className="w-24 px-3 py-2.5 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] focus:outline-none focus:border-[#338edc]"
 />
 <span className="text-[11px] text-[var(--text-tertiary)]">{t('leadFinder.perMonth')}</span>
 </div>
 </div>
 </div>

 {/* Footer */}
 <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--border-card)]">
 <button onClick={onClose} className="px-4 py-2 text-[13px] border border-[var(--border-card)] rounded-[10px] text-[var(--text-secondary)]">
 {t('leadFinder.cancel')}
 </button>
 <button
 onClick={handleSave}
 disabled={!name.trim() || saving}
 className="px-4 py-2 text-[13px] font-medium text-white bg-[#338edc] rounded-[10px] hover:bg-[#2b7bc4] disabled:opacity-50"
 >
 {saving ? t('leadFinder.saving') : t('leadFinder.save')}
 </button>
 </div>
 </div>
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════════════════
// RESULTS TABLE
// ═══════════════════════════════════════════════════════════════════════════

function scoreColor(score) {
 if (score >= 70) return 'bg-green-50 text-green-700';
 if (score >= 40) return 'bg-amber-50 text-amber-700';
 return 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)]';
}

const STATUS_LABELS = { all: 'All', new: 'New', enriched: 'Enriched', imported: 'Imported', rejected: 'Rejected' };

function ResultsTable({ profileId }) {
 const { t } = useLanguage();
 const [results, setResults] = useState([]);
 const [total, setTotal] = useState(0);
 const [loading, setLoading] = useState(true);
 const [selectedIds, setSelectedIds] = useState(new Set());
 const [statusFilter, setStatusFilter] = useState('all');
 const [searchStatus, setSearchStatus] = useState(null);
 const [detailId, setDetailId] = useState(null);
 const [importing, setImporting] = useState(false);
 const [enrichingId, setEnrichingId] = useState(null);
 const [researchingId, setResearchingId] = useState(null);

 const loadResults = useCallback(async () => {
 setLoading(true);
 try {
 const data = await api(`/api/lead-finder/results/${profileId}?status=${statusFilter}&sort=fit_score&order=DESC&limit=100`);
 setResults(data.results || []);
 setTotal(data.total || 0);
 } catch { setResults([]); setTotal(0); }
 setLoading(false);
 }, [profileId, statusFilter]);

 const pollStatus = useCallback(async () => {
 try {
 const data = await api(`/api/lead-finder/search/${profileId}/status`);
 setSearchStatus(data);
 if (data.last_run_status === 'running') {
 setTimeout(() => { loadResults(); pollStatus(); }, 3000);
 }
 } catch { /* ignore */ }
 }, [profileId, loadResults]);

 useEffect(() => { loadResults(); pollStatus(); }, [loadResults, pollStatus]);

 function toggleSelect(id) {
 setSelectedIds(prev => {
 const next = new Set(prev);
 next.has(id) ? next.delete(id) : next.add(id);
 return next;
 });
 }

 function toggleAll() {
 if (selectedIds.size === results.length) setSelectedIds(new Set());
 else setSelectedIds(new Set(results.filter(r => r.status !== 'imported').map(r => r.id)));
 }

 async function importSelected() {
 setImporting(true);
 await fetch(`${API}/api/lead-finder/results/import`, {
 method: 'POST', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ resultIds: Array.from(selectedIds) }),
 });
 setSelectedIds(new Set());
 setImporting(false);
 loadResults();
 }

 async function enrichOne(id) {
 setEnrichingId(id);
 await fetch(`${API}/api/lead-finder/results/${id}/enrich`, { method: 'POST', credentials: 'include' });
 setEnrichingId(null);
 loadResults();
 }

 async function researchOne(id) {
 setResearchingId(id);
 await fetch(`${API}/api/lead-finder/results/${id}/research`, { method: 'POST', credentials: 'include' });
 setResearchingId(null);
 loadResults();
 }

 async function rejectOne(id) {
 await fetch(`${API}/api/lead-finder/results/${id}/reject`, { method: 'POST', credentials: 'include' });
 loadResults();
 }

 return (
 <div>
 {/* Running banner */}
 {searchStatus?.last_run_status === 'running' && (
 <div className="flex items-center gap-2 mb-4 px-4 py-2.5 bg-blue-50 rounded-[10px] text-[13px] text-blue-700 border border-blue-100">
 <Loader className="w-3.5 h-3.5 animate-spin" /> {t('leadFinder.searching')}... {total > 0 && `${total} ${t('leadFinder.resultsFound')}`}
 </div>
 )}

 {/* Toolbar */}
 <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
 {/* Status Filter */}
 <div className="flex gap-1 border-b border-[var(--border-table,#F3F4F6)]">
 {['all', 'new', 'enriched', 'imported', 'rejected'].map(s => (
 <button
 key={s}
 onClick={() => setStatusFilter(s)}
 className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
 statusFilter === s
 ? 'border-[#338edc] text-[#338edc]'
 : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
 }`}
 >
 {t(`leadFinder.status_${s}`)}
 </button>
 ))}
 </div>

 <div className="flex items-center gap-2">
 <span className="text-[12px] text-[var(--text-tertiary)]">{total} {t('leadFinder.resultsFound')}</span>
 <button
 onClick={() => window.open(`${API}/api/lead-finder/results/${profileId}/export`, '_blank')}
 className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] border border-[var(--border-card)] rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]"
 >
 <Download className="w-3 h-3" /> CSV
 </button>
 </div>
 </div>

 {/* Bulk bar */}
 {selectedIds.size > 0 && (
 <div className="flex items-center gap-3 mb-3 px-4 py-2.5 rounded-xl bg-[#EBF4FD] border border-[#B8D8F5]">
 <span className="text-[13px] font-medium text-[#338edc]">
 {selectedIds.size} {t('leadFinder.selected')}
 </span>
 <div className="flex items-center gap-2 ml-auto">
 <button
 onClick={importSelected}
 disabled={importing}
 className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium text-white bg-[#338edc] hover:bg-[#2b7bc4] disabled:opacity-50"
 >
 {importing ? <Loader className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
 {t('leadFinder.importAsLeads')}
 </button>
 <button
 onClick={() => setSelectedIds(new Set())}
 className="p-1 hover:bg-[#D6E9FA] rounded"
 >
 <X className="w-4 h-4 text-[#338edc]" />
 </button>
 </div>
 </div>
 )}

 {/* Table */}
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-[12px] overflow-hidden">
 <table className="w-full">
 <thead>
 <tr className="border-b border-[var(--border-table,#F3F4F6)]">
 <th className="px-4 py-3 w-10">
 <input
 type="checkbox"
 checked={results.length > 0 && selectedIds.size === results.filter(r => r.status !== 'imported').length}
 onChange={toggleAll}
 className="w-4 h-4 rounded border-[var(--border-default,#E5E7EB)] accent-[#338edc] cursor-pointer"
 />
 </th>
 <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{t('leadFinder.fitScore')}</th>
 <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{t('common.company')}</th>
 <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{t('leadFinder.country')}</th>
 <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{t('leadFinder.industryCol')}</th>
 <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{t('common.contact')}</th>
 <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{t('leadFinder.shop')}</th>
 <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{t('leadFinder.status')}</th>
 <th className="px-4 py-3 w-28"></th>
 </tr>
 </thead>
 <tbody>
 {loading ? (
 <tr><td colSpan={9} className="text-center py-12 text-[var(--text-tertiary)] text-[13px]">Loading...</td></tr>
 ) : results.length === 0 ? (
 <tr><td colSpan={9} className="text-center py-12 text-[var(--text-tertiary)] text-[13px]">{t('common.noData')}</td></tr>
 ) : results.map(r => {
 const wa = r.website_analysis || {};
 return (
 <tr
 key={r.id}
 onClick={() => setDetailId(r.id)}
 className={`border-b border-[var(--border-table,#F3F4F6)] last:border-0 hover:bg-[var(--bg-card-hover)] cursor-pointer transition-colors ${selectedIds.has(r.id) ? 'bg-[#EBF4FD]' : ''}`}
 >
 <td className="px-4 py-3 w-10" onClick={e => e.stopPropagation()}>
 <input
 type="checkbox"
 checked={selectedIds.has(r.id)}
 onChange={() => toggleSelect(r.id)}
 disabled={r.status === 'imported'}
 className="w-4 h-4 rounded border-[var(--border-default,#E5E7EB)] accent-[#338edc] cursor-pointer disabled:opacity-40"
 />
 </td>
 {/* Score */}
 <td className="px-4 py-3">
 <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${scoreColor(r.fit_score)}`}>
 {r.fit_score}
 </span>
 </td>
 {/* Company */}
 <td className="px-4 py-3">
 <div className="text-[13px] font-medium text-[var(--text-primary)]">{r.company_name || '—'}</div>
 {r.domain && (
 <div className="text-[11px] text-[var(--text-tertiary)] flex items-center gap-1">
 <Globe className="w-2.5 h-2.5" /> {r.domain}
 </div>
 )}
 </td>
 {/* Country */}
 <td className="px-4 py-3 text-[13px] text-[var(--text-secondary)]">
 {r.country || '—'}
 </td>
 {/* Industry */}
 <td className="px-4 py-3">
 {r.industry ? (
 <span className="text-[11px] px-2 py-0.5 rounded-lg bg-[var(--bg-inset)] text-[var(--text-secondary)]">{r.industry}</span>
 ) : '—'}
 </td>
 {/* Contact */}
 <td className="px-4 py-3">
 {r.contact_name ? (
 <div>
 <div className="text-[13px] text-[var(--text-primary)]">{r.contact_name}</div>
 <div className="text-[11px] text-[var(--text-tertiary)]">{r.contact_title || ''}</div>
 </div>
 ) : <span className="text-[11px] text-[var(--text-tertiary)]">—</span>}
 </td>
 {/* Shop System */}
 <td className="px-4 py-3">
 {wa.shop_system ? (
 <span className="text-[11px] px-2 py-0.5 rounded-lg bg-cyan-50 text-cyan-700 font-medium">{wa.shop_system}</span>
 ) : wa.is_ecommerce ? (
 <span className="text-[11px] px-2 py-0.5 rounded-lg bg-green-50 text-green-600">E-Commerce</span>
 ) : <span className="text-[11px] text-[var(--text-tertiary)]">—</span>}
 </td>
 {/* Status */}
 <td className="px-4 py-3">
 <StatusBadge status={r.status} />
 </td>
 {/* Actions */}
 <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
 <div className="flex items-center gap-1">
 {r.status !== 'imported' && r.status !== 'rejected' && (
 <>
 <button
 onClick={() => enrichOne(r.id)}
 disabled={enrichingId === r.id}
 title={t('leadFinder.enrich')}
 className="p-1.5 rounded-lg text-[var(--text-tertiary)] hover:text-[#338edc] hover:bg-[#EBF4FD] transition-colors disabled:opacity-40"
 >
 {enrichingId === r.id ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
 </button>
 <button
 onClick={() => researchOne(r.id)}
 disabled={researchingId === r.id}
 title={t('leadFinder.aiResearch')}
 className="p-1.5 rounded-lg text-[var(--text-tertiary)] hover:text-[#338edc] hover:bg-[#EBF4FD] transition-colors disabled:opacity-40"
 >
 {researchingId === r.id ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
 </button>
 <button
 onClick={() => rejectOne(r.id)}
 title={t('leadFinder.reject')}
 className="p-1.5 rounded-lg text-[var(--text-tertiary)] hover:text-red-500 hover:bg-red-50 transition-colors"
 >
 <XCircle className="w-3.5 h-3.5" />
 </button>
 </>
 )}
 </div>
 </td>
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>

 {/* Detail Modal */}
 {detailId && (
 <ResultDetail
 resultId={detailId}
 profileId={profileId}
 onClose={() => setDetailId(null)}
 onEnrich={enrichOne}
 onResearch={researchOne}
 onReload={loadResults}
 />
 )}
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════════════════
// STATUS BADGE
// ═══════════════════════════════════════════════════════════════════════════

function StatusBadge({ status }) {
 const colors = {
 new: 'bg-blue-50 text-blue-600',
 enriching: 'bg-amber-50 text-amber-600',
 enriched: 'bg-[#EBF4FD] text-[#338edc]',
 reviewed: 'bg-indigo-50 text-indigo-600',
 imported: 'bg-green-50 text-green-700',
 rejected: 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary,#6B7280)]',
 };
 const labels = {
 new: 'Neu', enriching: 'Enriching...', enriched: 'Enriched',
 reviewed: 'Reviewed', imported: 'Imported', rejected: 'Rejected',
 };
 return (
 <span className={`inline-flex text-[10px] font-semibold px-2 py-0.5 rounded-full ${colors[status] || colors.new}`}>
 {labels[status] || status}
 </span>
 );
}

// ═══════════════════════════════════════════════════════════════════════════
// RESULT DETAIL MODAL
// ═══════════════════════════════════════════════════════════════════════════

function ResultDetail({ resultId, profileId, onClose, onEnrich, onResearch, onReload }) {
 const { t } = useLanguage();
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(true);
 const [importingOne, setImportingOne] = useState(false);

 useEffect(() => {
 setLoading(true);
 api(`/api/lead-finder/results/${profileId}/detail/${resultId}`)
 .then(d => { setData(d); setLoading(false); })
 .catch(() => setLoading(false));
 }, [resultId, profileId]);

 async function importOne() {
 setImportingOne(true);
 await fetch(`${API}/api/lead-finder/results/import`, {
 method: 'POST', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ resultIds: [resultId] }),
 });
 setImportingOne(false);
 onReload();
 onClose();
 }

 if (loading || !data) {
 return (
 <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
 <div className="bg-[var(--bg-card)] rounded-[12px] p-8 border border-[var(--border-card)]" onClick={e => e.stopPropagation()}>
 <Loader className="w-6 h-6 animate-spin text-[#338edc] mx-auto" />
 </div>
 </div>
 );
 }

 const wa = data.website_analysis || {};
 const ai = data.ai_research || {};

 return (
 <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
 <div className="bg-[var(--bg-card)] rounded-[12px] border border-[var(--border-card)] w-full max-w-2xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
 {/* Header */}
 <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card)]">
 <div className="flex items-center gap-3">
 <div>
 <div className="text-[15px] font-semibold text-[var(--text-primary)]">{data.company_name}</div>
 {data.domain && (
 <a href={`https://${data.domain}`} target="_blank" rel="noreferrer"
 className="text-[12px] text-[#338edc] hover:underline flex items-center gap-1">
 <Globe className="w-3 h-3" /> {data.domain} <ExternalLink className="w-2.5 h-2.5" />
 </a>
 )}
 </div>
 <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[12px] font-bold ${scoreColor(data.fit_score)}`}>
 {data.fit_score}/100
 </span>
 </div>
 <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
 <X className="w-4 h-4" />
 </button>
 </div>

 <div className="p-6 overflow-y-auto max-h-[calc(85vh-130px)] space-y-5">
 {/* Two Column Grid */}
 <div className="grid grid-cols-2 gap-5">
 {/* LEFT: Company Info */}
 <div className="space-y-4">
 <SectionTitle>{t('leadFinder.companyInfo')}</SectionTitle>
 <InfoRow label={t('leadFinder.country')} value={data.country} />
 <InfoRow label={t('leadFinder.city')} value={data.city} />
 <InfoRow label={t('leadFinder.industryCol')} value={data.industry} />
 <InfoRow label={t('leadFinder.employees')} value={data.employee_count} />
 <InfoRow label={t('leadFinder.revenue')} value={data.revenue_estimate} />
 <InfoRow label={t('leadFinder.founded')} value={data.founded_year} />
 {data.linkedin_url && (
 <InfoRow label="LinkedIn" value={
 <a href={data.linkedin_url} target="_blank" rel="noreferrer" className="text-[#338edc] hover:underline text-[12px] flex items-center gap-1">
 LinkedIn <ExternalLink className="w-2.5 h-2.5" />
 </a>
 } />
 )}

 {/* Technologies */}
 {(data.technologies || []).length > 0 && (
 <div>
 <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-1">{t('leadFinder.technologies')}</div>
 <div className="flex flex-wrap gap-1">
 {data.technologies.map((tech, i) => (
 <span key={i} className="text-[10px] px-2 py-0.5 rounded-lg bg-cyan-50 text-cyan-700 font-medium">{tech}</span>
 ))}
 </div>
 </div>
 )}
 </div>

 {/* RIGHT: Contact */}
 <div className="space-y-4">
 <SectionTitle>{t('common.contact')}</SectionTitle>
 {data.contact_name ? (
 <>
 <InfoRow label={t('common.name')} value={data.contact_name} />
 <InfoRow label={t('leadFinder.title')} value={data.contact_title} />
 {data.contact_email && <InfoRow label={t('common.email')} value={
 <a href={`mailto:${data.contact_email}`} className="text-[#338edc] hover:underline text-[12px]">{data.contact_email}</a>
 } />}
 {data.contact_phone && <InfoRow label={t('common.phone')} value={data.contact_phone} />}
 {data.contact_linkedin && <InfoRow label="LinkedIn" value={
 <a href={data.contact_linkedin} target="_blank" rel="noreferrer" className="text-[#338edc] hover:underline text-[12px] flex items-center gap-1">
 Profil <ExternalLink className="w-2.5 h-2.5" />
 </a>
 } />}
 </>
 ) : (
 <div className="text-[12px] text-[var(--text-tertiary)]">{t('leadFinder.noContact')}</div>
 )}
 </div>
 </div>

 {/* Website Analysis */}
 {wa.analyzable !== undefined && (
 <div>
 <SectionTitle>{t('leadFinder.websiteAnalysis')}</SectionTitle>
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
 <MiniCard label="E-Commerce" value={wa.is_ecommerce ? 'Ja' : 'Nein'} positive={wa.is_ecommerce} />
 <MiniCard label="Shop-System" value={wa.shop_system || '—'} positive={!!wa.shop_system} />
 <MiniCard label={t('leadFinder.cart')} value={wa.has_cart ? 'Ja' : 'Nein'} positive={wa.has_cart} />
 <MiniCard label={t('leadFinder.shipping')} value={wa.has_shipping_info ? 'Ja' : 'Nein'} positive={wa.has_shipping_info} />
 </div>
 {(wa.technologies_detected || []).length > 0 && (
 <div className="flex flex-wrap gap-1 mt-2">
 {wa.technologies_detected.map((t, i) => (
 <span key={i} className="text-[10px] px-2 py-0.5 rounded-lg bg-[var(--bg-inset)] text-[var(--text-secondary)]">{t}</span>
 ))}
 </div>
 )}
 </div>
 )}

 {/* AI Research */}
 {ai.summary && (
 <div>
 <SectionTitle>{t('leadFinder.aiResearch')}</SectionTitle>
 <div className="bg-[var(--bg-inset)] rounded-[10px] p-4 space-y-3 mt-2">
 <p className="text-[13px] text-[var(--text-primary)]">{ai.summary}</p>

 <div className="grid grid-cols-2 gap-3">
 <div>
 <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-1">E-Commerce</div>
 <div className="text-[12px] text-[var(--text-primary)]">{ai.ecommerce_activity || '—'}</div>
 </div>
 <div>
 <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-1">Fulfillment</div>
 <div className="text-[12px] text-[var(--text-primary)]">{ai.current_fulfillment || '—'}</div>
 </div>
 <div>
 <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-1">Fit</div>
 <div className="text-[12px] text-[var(--text-primary)]">{ai.fit_for_weship || '—'} — {ai.fit_reason || ''}</div>
 </div>
 <div>
 <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-1">Pakete/Monat</div>
 <div className="text-[12px] text-[var(--text-primary)]">{ai.estimated_monthly_parcels || '—'}</div>
 </div>
 </div>

 {(ai.growth_signals || []).length > 0 && (
 <div>
 <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-1">{t('leadFinder.growthSignals')}</div>
 <ul className="text-[12px] text-[var(--text-primary)] space-y-0.5">
 {ai.growth_signals.map((s, i) => <li key={i} className="flex items-start gap-1.5"><Zap className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" />{s}</li>)}
 </ul>
 </div>
 )}

 {(ai.conversation_starters || []).length > 0 && (
 <div>
 <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-1">{t('leadFinder.conversationStarters')}</div>
 <ul className="text-[12px] text-[var(--text-primary)] space-y-0.5">
 {ai.conversation_starters.map((s, i) => <li key={i} className="flex items-start gap-1.5"><span className="text-[#338edc]">•</span>{s}</li>)}
 </ul>
 </div>
 )}
 </div>
 </div>
 )}
 </div>

 {/* Footer Actions */}
 <div className="flex items-center justify-between px-6 py-4 border-t border-[var(--border-card)]">
 <div className="flex items-center gap-2">
 {data.status !== 'imported' && data.status !== 'rejected' && (
 <>
 <button
 onClick={() => onEnrich(resultId)}
 className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] border border-[var(--border-card)] rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]"
 >
 <Mail className="w-3 h-3" /> {t('leadFinder.enrich')}
 </button>
 <button
 onClick={() => onResearch(resultId)}
 className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] border border-[var(--border-card)] rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]"
 >
 <Sparkles className="w-3 h-3" /> {t('leadFinder.aiResearch')}
 </button>
 </>
 )}
 </div>
 <div className="flex items-center gap-2">
 <button onClick={onClose} className="px-4 py-2 text-[13px] border border-[var(--border-card)] rounded-[10px] text-[var(--text-secondary)]">
 {t('leadFinder.cancel')}
 </button>
 {data.status !== 'imported' && (
 <button
 onClick={importOne}
 disabled={importingOne}
 className="flex items-center gap-1.5 px-4 py-2 text-[13px] font-medium text-white bg-[#338edc] rounded-[10px] hover:bg-[#2b7bc4] disabled:opacity-50"
 >
 {importingOne ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
 {t('leadFinder.importAsLead')}
 </button>
 )}
 </div>
 </div>
 </div>
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════════════════
// APOLLO KEY BANNER
// ═══════════════════════════════════════════════════════════════════════════

function ApolloKeyBanner({ onGoToSettings }) {
 const { t } = useLanguage();
 return (
 <div className="flex items-center gap-3 mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-[12px]">
 <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
 <div className="flex-1">
 <div className="text-[13px] font-medium text-amber-800">{t('leadFinder.noApiKey')}</div>
 <div className="text-[11px] text-amber-600 mt-0.5">{t('leadFinder.noApiKeyDesc')}</div>
 </div>
 <button
 onClick={onGoToSettings}
 className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-amber-800 bg-amber-100 border border-amber-300 rounded-lg hover:bg-amber-200 transition-colors"
 >
 <Settings className="w-3 h-3" /> {t('leadFinder.settings')}
 </button>
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════════════════
// SETTINGS TAB (Admin only)
// ═══════════════════════════════════════════════════════════════════════════

function LeadFinderSettings({ onKeyChanged }) {
 const { t } = useLanguage();
 const [settings, setSettings] = useState(null);
 const [apiKey, setApiKey] = useState('');
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [testing, setTesting] = useState(false);
 const [testResult, setTestResult] = useState(null);
 const [credits, setCredits] = useState(null);

 useEffect(() => {
 Promise.all([
 api('/api/lead-finder/settings'),
 api('/api/lead-finder/credits'),
 ]).then(([s, c]) => {
 setSettings(s);
 setCredits(c);
 setLoading(false);
 }).catch(() => setLoading(false));
 }, []);

 async function saveSettings(updates) {
 setSaving(true);
 await fetch(`${API}/api/lead-finder/settings`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(updates),
 });
 const s = await api('/api/lead-finder/settings');
 setSettings(s);
 setSaving(false);
 onKeyChanged();
 }

 async function testConnection() {
 setTesting(true);
 setTestResult(null);
 try {
 const res = await fetch(`${API}/api/lead-finder/test-apollo`, {
 method: 'POST', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ api_key: apiKey || undefined }),
 });
 const data = await res.json();
 setTestResult(data);
 } catch {
 setTestResult({ success: false, error: 'Verbindung fehlgeschlagen' });
 }
 setTesting(false);
 }

 async function deleteKey() {
 if (!confirm(t('leadFinder.deleteKeyConfirm'))) return;
 await fetch(`${API}/api/lead-finder/apollo-key`, { method: 'DELETE', credentials: 'include' });
 const s = await api('/api/lead-finder/settings');
 setSettings(s);
 setApiKey('');
 onKeyChanged();
 }

 if (loading) return <div className="text-center py-12 text-[var(--text-tertiary)] text-[13px]">Loading...</div>;

 return (
 <div className="space-y-6">
 {/* Apollo API Key */}
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-[12px] p-6">
 <div className="flex items-center gap-2 mb-4">
 <Key className="w-4 h-4 text-[#338edc]" />
 <span className="text-[14px] font-semibold text-[var(--text-primary)]">{t('leadFinder.apolloApiKey')}</span>
 </div>

 {settings?.has_api_key ? (
 <div className="space-y-3">
 <div className="flex items-center gap-3">
 <div className="flex-1 px-3 py-2.5 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-inset)] text-[13px] text-[var(--text-secondary)] font-mono">
 {settings.api_key_masked}
 </div>
 <button
 onClick={deleteKey}
 className="flex items-center gap-1.5 px-3 py-2 text-[12px] text-red-600 border border-red-200 rounded-[10px] hover:bg-red-50 transition-colors"
 >
 <Trash2 className="w-3 h-3" /> {t('leadFinder.removeKey')}
 </button>
 </div>
 <div className="flex items-center gap-1.5 text-[11px] text-green-600">
 <Check className="w-3 h-3" /> {t('leadFinder.keyConfigured')}
 </div>
 </div>
 ) : (
 <div className="space-y-3">
 <div className="flex items-center gap-2">
 <input
 type="password"
 value={apiKey}
 onChange={e => setApiKey(e.target.value)}
 placeholder="Apollo API Key eingeben..."
 className="flex-1 px-3 py-2.5 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] font-mono focus:outline-none focus:border-[#338edc]"
 />
 <button
 onClick={() => saveSettings({ api_key: apiKey })}
 disabled={!apiKey.trim() || saving}
 className="px-4 py-2 text-[13px] font-medium text-white bg-[#338edc] rounded-[10px] hover:bg-[#2b7bc4] disabled:opacity-50"
 >
 {saving ? t('leadFinder.saving') : t('leadFinder.save')}
 </button>
 </div>
 <p className="text-[11px] text-[var(--text-tertiary)]">{t('leadFinder.apolloKeyHint')}</p>
 </div>
 )}

 {/* Test Connection */}
 <div className="mt-4 pt-4 border-t border-[var(--border-card)]">
 <button
 onClick={testConnection}
 disabled={testing || (!settings?.has_api_key && !apiKey.trim())}
 className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium border border-[var(--border-card)] rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] disabled:opacity-50 transition-colors"
 >
 {testing ? <Loader className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
 {t('leadFinder.testConnection')}
 </button>
 {testResult && (
 <div className={`mt-2 px-3 py-2 rounded-[10px] text-[12px] ${
 testResult.success
 ? 'bg-green-50 text-green-700 border border-green-200'
 : 'bg-red-50 text-red-600 border border-red-200'
 }`}>
 {testResult.success ? testResult.message : testResult.error}
 </div>
 )}
 </div>
 </div>

 {/* Search Settings */}
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-[12px] p-6">
 <div className="text-[14px] font-semibold text-[var(--text-primary)] mb-4">{t('leadFinder.searchSettings')}</div>

 {/* Max Results */}
 <div className="mb-5">
 <label className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] block mb-1.5">
 {t('leadFinder.maxResults')}
 </label>
 <select
 value={settings?.max_results || 25}
 onChange={e => saveSettings({ max_results: parseInt(e.target.value) })}
 className="px-3 py-2.5 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] focus:outline-none focus:border-[#338edc]"
 >
 <option value={10}>10</option>
 <option value={25}>25</option>
 <option value={50}>50</option>
 <option value={100}>100</option>
 </select>
 <p className="text-[11px] text-[var(--text-tertiary)] mt-1">{t('leadFinder.maxResultsHint')}</p>
 </div>

 {/* Auto Website Analysis */}
 <div className="flex items-center justify-between py-3 border-t border-[var(--border-card)]">
 <div>
 <div className="text-[13px] font-medium text-[var(--text-primary)]">{t('leadFinder.autoWebsiteAnalysis')}</div>
 <div className="text-[11px] text-[var(--text-tertiary)]">{t('leadFinder.autoWebsiteAnalysisDesc')}</div>
 </div>
 <ToggleSwitch
 checked={settings?.auto_website_analysis !== false}
 onChange={() => saveSettings({ auto_website_analysis: !(settings?.auto_website_analysis !== false) })}
 />
 </div>

 {/* Auto AI Research */}
 <div className="flex items-center justify-between py-3 border-t border-[var(--border-card)]">
 <div>
 <div className="text-[13px] font-medium text-[var(--text-primary)]">{t('leadFinder.autoAiResearch')}</div>
 <div className="text-[11px] text-[var(--text-tertiary)]">{t('leadFinder.autoAiResearchDesc')}</div>
 </div>
 <ToggleSwitch
 checked={settings?.auto_ai_research === true}
 onChange={() => saveSettings({ auto_ai_research: !(settings?.auto_ai_research === true) })}
 />
 </div>
 </div>

 {/* Credit Usage */}
 {credits && (
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-[12px] p-6">
 <div className="flex items-center gap-2 text-[14px] font-semibold text-[var(--text-primary)] mb-3">
 <Zap size={16} className="text-[#6366F1]" />
 {t('leadFinder.creditUsage')}
 </div>
 <div className="flex items-baseline gap-2">
 <span className="text-[28px] font-bold text-[var(--text-primary)]">{credits.apollo_credits_used}</span>
 <span className="text-[13px] text-[var(--text-secondary)]">{t('leadFinder.creditsUsed')}</span>
 </div>
 <p className="text-[11px] text-[var(--text-tertiary)] mt-1">{credits.note}</p>
 </div>
 )}
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

function SectionTitle({ children }) {
 return <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{children}</div>;
}

function InfoRow({ label, value }) {
 if (!value) return null;
 return (
 <div className="flex items-start gap-2">
 <span className="text-[11px] text-[var(--text-tertiary)] w-20 shrink-0">{label}</span>
 <span className="text-[12px] text-[var(--text-primary)]">{value}</span>
 </div>
 );
}

function MiniCard({ label, value, positive }) {
 return (
 <div className={`px-3 py-2 rounded-[10px] border ${positive ? 'border-green-200 bg-green-50' : 'border-[var(--border-card)] bg-[var(--bg-card)]'}`}>
 <div className="text-[10px] text-[var(--text-tertiary)] mb-0.5">{label}</div>
 <div className={`text-[12px] font-medium ${positive ? 'text-green-700' : 'text-[var(--text-primary)]'}`}>{value}</div>
 </div>
 );
}
